import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/* =========================================================================
   TEMA
   Tres estados, no dos: "system" no estampa atributo y deja mandar a
   prefers-color-scheme; "light" y "dark" sí lo estampan y le ganan al
   sistema. Es el mismo contrato que asumen los tokens en tokens.css.

   El PREDETERMINADO es claro, no "system": un panel de administración se
   consulta a plena luz y con proyector, y heredar el modo oscuro del sistema
   operativo dejaba el tablero negro sin que nadie lo hubiera pedido. Quien
   quiera seguir al sistema lo elige en Ajustes.
   ======================================================================== */

const ThemeCtx = createContext(null);
const KEY = "vpy.theme";
const POR_DEFECTO = "light";

const read = () => {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" || v === "system" ? v : POR_DEFECTO;
  } catch {
    return POR_DEFECTO;
  }
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(read);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
    // "system" también se guarda: ahora es una elección explícita y no el
    // valor por omisión, así que borrar la clave lo devolvería a claro.
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* almacenamiento bloqueado */
    }
  }, [theme]);

  /** Qué se está viendo realmente, resolviendo "system". */
  const resolved = useMemo(() => {
    if (theme !== "system") return theme;
    if (typeof window === "undefined" || !window.matchMedia) return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }, [theme]);

  const toggle = useCallback(() => {
    setThemeState(resolved === "dark" ? "light" : "dark");
  }, [resolved]);

  const value = useMemo(
    () => ({ theme, resolved, setTheme: setThemeState, toggle }),
    [theme, resolved, toggle],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => {
  const ctx = useContext(ThemeCtx);
  if (!ctx)
    throw new Error("useTheme necesita estar dentro de <ThemeProvider>");
  return ctx;
};
